if (!customElements.get('carousel-dots')) {
  class CarouselDots extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      // Wait for DOM to be fully rendered and layout to be calculated
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.init();
        }, 100);
      });
    }

    init() {
      this.slider = this.querySelector('.slider');
      this.dots = this.querySelectorAll('.slider-counter__link--dots');
      // Support both slideshow slides and product grid items
      this.slides = this.querySelectorAll('.slideshow__slide');
      if (!this.slides.length) {
        this.slides = this.slider ? this.slider.querySelectorAll('.grid__item') : [];
      }
      
      if (!this.slider || !this.dots.length) return;
      
      // Wait for images and layout to be ready
      if (this.slides[0] && this.slides[0].offsetWidth === 0) {
        // Slides not yet rendered, wait a bit longer
        setTimeout(() => {
          this.updateDotsVisibility();
          this.attachDotHandlers();
        }, 200);
      } else {
        this.updateDotsVisibility();
        this.attachDotHandlers();
      }
      
      // Update on resize
      window.addEventListener('resize', this.debounce(() => {
        this.updateDotsVisibility();
      }, 150));
    }
  
    attachDotHandlers() {
      // Use capture phase to intercept before slideshow component
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          // Check if this is the last visible dot by finding the last dot that's not hidden
          const visibleDots = Array.from(this.dots).filter(d => d.style.display !== 'none');
          const lastVisibleDot = visibleDots[visibleDots.length - 1];
          
          // If this is the last visible dot, scroll to the end
          if (dot === lastVisibleDot) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.scrollToLastSlide();
          }
        }, true); // Use capture phase
      });
    }
    
    scrollToLastSlide() {
      const lastSlide = this.slides[this.slides.length - 1];
      if (!lastSlide) return;
      
      // Get padding
      const sliderStyles = window.getComputedStyle(this.slider);
      const paddingLeft = parseFloat(sliderStyles.paddingLeft) || 0;
      const paddingRight = parseFloat(sliderStyles.paddingRight) || 0;
      
      // Scroll to show the last slide fully
      const sliderWidth = this.slider.clientWidth;
      const scrollLeft = lastSlide.offsetLeft + lastSlide.offsetWidth - sliderWidth + paddingRight;
      
      this.slider.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth'
      });
    }
    
    updateDotsVisibility() {
      if (!this.slider || !this.slides.length) return;
      
      const sliderWidth = this.slider.clientWidth;
      const firstSlide = this.slides[0];
      const lastSlide = this.slides[this.slides.length - 1];
      if (!firstSlide || !lastSlide) return;
      
      const slideWidth = firstSlide.offsetWidth;
      const totalSlides = this.slides.length;
      const gap = 16;
      
      // Calculate the total scrollable width
      const totalWidth = (slideWidth * totalSlides) + (gap * (totalSlides - 1));
      const scrollableWidth = totalWidth - sliderWidth;
      
      // If everything fits in view, no dots needed
      if (scrollableWidth <= 0) {
        this.dots.forEach(dot => dot.style.display = 'none');
        return;
      }
      
      // Calculate how many "pages" we need based on scroll positions
      const scrollPerPage = slideWidth + gap;
      let visibleDots = Math.ceil(scrollableWidth / scrollPerPage) + 1;
      
      // Calculate how much of the last slide would be visible when scrolled to second-to-last position
      // Second-to-last scroll position would be: (visibleDots - 2) * scrollPerPage
      const secondToLastScrollPos = (visibleDots - 2) * scrollPerPage;
      const lastSlideLeft = lastSlide.offsetLeft;
      const lastSlideWidth = lastSlide.offsetWidth;
      
      // At second-to-last position, viewport shows from secondToLastScrollPos to (secondToLastScrollPos + sliderWidth)
      const viewportLeftAtSecondToLast = secondToLastScrollPos;
      const viewportRightAtSecondToLast = secondToLastScrollPos + sliderWidth;
      
      // Calculate how much of last slide would be visible
      const lastSlideRight = lastSlideLeft + lastSlideWidth;
      const lastSlideVisibleWidth = Math.max(0, Math.min(lastSlideRight, viewportRightAtSecondToLast) - Math.max(lastSlideLeft, viewportLeftAtSecondToLast));
      
      // If more than 50% of last slide would be visible at second-to-last position, remove the last dot
      if (lastSlideVisibleWidth > lastSlideWidth * 0.5) {
        visibleDots = visibleDots - 1;
      }
      
      // Ensure we don't show more dots than slides
      visibleDots = Math.min(Math.max(1, visibleDots), totalSlides);
      
      // Hide dots beyond visibleDots
      this.dots.forEach((dot, index) => {
        if (index < visibleDots) {
          dot.style.display = '';
        } else {
          dot.style.display = 'none';
        }
      });
    }
    
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  }
  
  customElements.define('carousel-dots', CarouselDots);
}
